import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../../lib/utils';

/**
 * Badge — Discovr AI design system (Figma "Badge" component).
 *
 * Pill-shaped badge built on the additive Figma token layer
 * (see design-system/tokens.css). Variants, sizes and the per-size type
 * scale mirror the Figma component 1:1:
 *   - sm → Caption/Medium (DM Sans 12/16, weight 500), padding-xs, height 22px
 *   - md → Body/Small/Semibold (DM Sans 14/20, weight 600), padding-sm
 *   - lg → Body/Medium/Semibold (DM Sans 16/24, weight 600), padding-md
 *
 * Hover styling: `default` and `destructive` use the explicit Figma
 * `*-default-hover` tokens. Figma did not expose distinct hover tokens for
 * `secondary`/`outline`, so those use a light, conventional hover. Hover only
 * applies when rendered as an anchor (`[a&]`), matching the base @discovr/ui Badge.
 *
 * Text color uses the `text-[color:var(--…)]` form and the per-size type scale
 * uses arbitrary lengths on purpose: a custom `text-<color>` and a custom
 * `text-<size>` utility would otherwise collide in tailwind-merge (same `text-`
 * group) and one would be dropped. The two arbitrary forms resolve to distinct
 * groups (text-color vs font-size), so both survive.
 */
const badgeVariants = cva(
  // No base border: default/secondary/destructive are borderless in Figma; the
  // outline variant adds its own.
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full py-0.5 font-dm-sans transition-colors [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default:
          'bg-background-primary-default text-(--foreground-primary-default) [a&]:hover:bg-background-primary-default-hover',
        secondary:
          'bg-background-secondary-default text-(--foreground-secondary-default) [a&]:hover:opacity-90',
        outline:
          'border border-(--border-default) text-(--foreground-default) [a&]:hover:bg-background-secondary-default',
        destructive:
          'bg-background-destructive-default text-(--white) [a&]:hover:bg-background-destructive-default-hover',
      },
      size: {
        // sm → Caption/Medium · md → Body/Small/Semibold · lg → Body/Medium/Semibold
        sm: 'h-[22px] px-(--padding-xs) text-[12px] leading-[16px] font-medium',
        md: 'px-(--padding-sm) text-[14px] leading-[20px] font-semibold',
        lg: 'px-(--padding-md) text-[16px] leading-[24px] font-semibold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
);

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
  };

function Badge({ className, variant, size, asChild = false, ...props }: BadgeProps) {
  const Comp = (asChild ? Slot.Root : 'span') as React.ElementType;

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
export type { BadgeProps };
